import { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "../assets/styles/ProductSlider.css";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const ProductSlider = ({ data = [], heading }) => {
  // Added default empty array for data
  const [activeHover, setActiveHover] = useState(-1);
  const swiperRef = useRef(null);
  const containerRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  useEffect(() => {
    // Ensure containerRef.current exists and has querySelectorAll method
    if (
      containerRef.current &&
      typeof containerRef.current.querySelectorAll === "function"
    ) {
      const cards = containerRef.current.querySelectorAll(".product-card-item"); // Use a specific class for items

      if (cards.length > 0) {
        // Only run animation if cards exist
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }
  }, [data]); // Re-run if data changes

  // Handle case where data might be empty or not yet loaded
  if (!data || data.length === 0) {
    // return null; // Or render a placeholder/message
    return (
      <div className="slider-outer-container font-poppins py-10">
        <p>No products to display.</p>
      </div>
    );
  }

  return (
    <div className="slider-outer-container font-poppins" ref={containerRef}>
      <div className="relative mx-auto px-4 lg:px-0 pt-16 pb-24 product-card-container">
        {heading && (
          <h1 className="text-left text-4xl pb-10 font-medium capitalize font-mono">
            {heading}
          </h1>
        )}
        <Swiper
          ref={swiperRef}
          modules={[Navigation]}
          spaceBetween={30}
          slidesPerView={4}
          breakpoints={{
            320: { slidesPerView: 1 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
          }}
          navigation={{
            prevEl: ".custom-prev",
            nextEl: ".custom-next",
          }}
          onSlideChange={handleSlideChange}
          onInit={(swiper) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          className="!overflow-visible"
        >
          {data.map((product, index) => (
            // Use _id if available, otherwise fallback to id or index
            <SwiperSlide
              key={product._id || product.id || index}
              className="product-card-item"
            >
              {/* Use _id for link */}
              <Link to={`/products/${product._id || product.id}`}>
                <div
                  className="product-card group relative h-96 cursor-pointer overflow-hidden"
                  onMouseEnter={() => setActiveHover(index)}
                  onMouseLeave={() => setActiveHover(-1)}
                  style={{
                    backgroundImage: `url(${product.bannerImageUrl})`,
                    backgroundPosition: "center",
                    backgroundSize: "cover", // optional: makes the image cover the area nicely
                    backgroundRepeat: "no-repeat", // optional: prevents repeating the image
                  }}
                >
                  {/* Card Content: Ensure all fields exist */}
                  <div className="card-content">
                    {product.sector && (
                      <div className="category-tag">{product.sector}</div>
                    )}

                    <div
                      className={`brand-overlay ${
                        activeHover === index ? "active" : ""
                      }`}
                    >
                      {product.brand || ""} {/* Display brand or empty */}
                    </div>

                    <div className="product-info">
                      <h3 className="truncate-text">
                        {product.title || "No Title"}
                      </h3>
                      <p className="truncate-text"> {product.subtitle || ""}</p>
                    </div>

                    <div
                      className={`hover-footer ${
                        activeHover === index ? "active" : ""
                      }`}
                    >
                      {product.buttonText || "View Details"}{" "}
                      {/* Default button text */}
                    </div>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Arrows (Keep as is) */}
        <button
          className={`custom-prev ... ${
            isBeginning ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          ...
        </button>
        <button
          className={`custom-next ... ${
            isEnd ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          ...
        </button>
      </div>
    </div>
  );
};

export default ProductSlider;
